<template>
  <button 
    :class="[
      'p-button p-component', 
      $attrs.class,
      { 'p-button-success': type === 'success' },
      { 'p-button-loading': loading }
    ]" 
    :type="type" 
    @click="handleClick" 
    :disabled="disabled || loading"
    v-bind="$attrs"
  >
    <span v-if="loading" class="spinner">
      <i class="pi pi-spin pi-spinner"></i>
    </span>
    <i v-else-if="icon" :class="iconClasses"></i>
    <span v-if="label" class="p-button-label">{{ label }}</span>
    <slot></slot>
  </button>
</template>

<script>
export default {
  name: 'CustomButton',
  props: {
    label: {
      type: String,
      default: null
    },
    icon: {
      type: String,
      default: null
    },
    type: {
      type: String,
      default: 'button'
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['click'],
  computed: {
    iconClasses() {
      if (!this.icon) return '';

      // Si el icono ya incluye 'pi', usarlo directamente
      if (this.icon.includes('pi')) {
        return this.icon;
      }

      // Si no incluye 'pi', agregarlo
      return `pi ${this.icon}`;
    }
  },
  methods: {
    handleClick(event) {
      if (!this.loading && !this.disabled) {
        this.$emit('click', event);
      }
    }
  }
}
</script>

<style scoped>
/* Reset de estilos del botón base - Look & Feel Premium */
.p-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem 1.25rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  border-radius: 8px; /* Bordes más redondeados */
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #e2e8f0;
  gap: 0.6rem;
  min-height: 2.5rem;
  background: white;
  color: #475569;
  letter-spacing: 0.025em;
}

/* Modo Texto (Minimalista para tablas) */
.p-button-text {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
  min-width: 2.4rem;
  height: 2.4rem;
}

.p-button-text.p-button-success { color: #10b981 !important; }
.p-button-text.p-button-warning { color: #f59e0b !important; }
.p-button-text.p-button-info { color: #3b82f6 !important; }
.p-button-text.p-button-danger { color: #ef4444 !important; }

/* Botones Sólidos de Acción Principal (Pill/Premium) */
.p-button-success:not(.p-button-text) {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  border: none !important;
  color: white !important;
  box-shadow: 0 4px 6px -1px rgba(16, 185, 129, 0.2);
}

.p-button-success:not(.p-button-text):hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(16, 185, 129, 0.3);
}

/* Estilo Outlined */
.p-button-outlined {
  background: transparent !important;
  border: 2px solid #10b981 !important;
  color: #10b981 !important;
}

.p-button-outlined:hover {
  background: #f0fdf4 !important;
}

.p-button-secondary {
  background: #f8fafc !important;
  border-color: #e2e8f0 !important;
}

.p-button:active {
  transform: scale(0.95) !important;
}

.p-button i.pi {
  font-size: 1.1rem;
}

.spinner {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
